import { useState, useEffect } from 'react'
import { Users, Search, ChevronLeft, ChevronRight, Crown, MapPin, Download, Zap, Infinity, MoreVertical, X } from 'lucide-react'
import { supabase } from '../../utils/supabase'

export default function UsersTable() {
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [exporting, setExporting] = useState(false)
    const [upgradeModal, setUpgradeModal] = useState({ open: false, user: null, plan: null })
    const [upgrading, setUpgrading] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState(null)
    const [filters, setFilters] = useState({
        status: 'all', // all, free, pro
        dateFrom: '',
        dateTo: '',
        search: '',
    })
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 10,
        total: 0,
    })

    useEffect(() => {
        fetchUsers()
    }, [filters, pagination.page])

    const fetchUsers = async () => {
        setLoading(true)
        try {
            // First get profiles
            let profileQuery = supabase
                .from('profiles')
                .select('*', { count: 'exact' })
                .order('created_at', { ascending: false })

            // Apply search filter
            if (filters.search) {
                profileQuery = profileQuery.ilike('email', `%${filters.search}%`)
            }

            // Apply date filters
            if (filters.dateFrom) {
                profileQuery = profileQuery.gte('created_at', filters.dateFrom)
            }
            if (filters.dateTo) {
                profileQuery = profileQuery.lte('created_at', filters.dateTo + 'T23:59:59')
            }

            // Pagination
            const from = (pagination.page - 1) * pagination.perPage
            const to = from + pagination.perPage - 1
            profileQuery = profileQuery.range(from, to)

            const { data: profiles, error: profileError, count } = await profileQuery

            if (profileError) {
                console.error('Supabase profiles error:', profileError)
                throw profileError
            }

            // Now fetch subscriptions for these users
            const userIds = (profiles || []).map(p => p.id)

            let subscriptionData = []
            if (userIds.length > 0) {
                const { data: subs, error: subError } = await supabase
                    .from('subscriptions')
                    .select('*')
                    .in('user_id', userIds)

                if (subError) {
                    console.error('Supabase subscriptions error:', subError)
                } else {
                    subscriptionData = subs || []
                }
            }

            // Merge profiles with subscription data
            let usersWithSubs = (profiles || []).map(profile => {
                // Find this user's subscription
                const subscription = subscriptionData.find(s =>
                    s.user_id === profile.id || s.email === profile.email
                )

                // Determine if Pro: validate billing dates as safety net
                let isPro = false
                if (subscription) {
                    const GRACE_DAYS = 7
                    const graceCutoff = new Date()
                    graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)

                    if (subscription.plan === 'lifetime' && subscription.status === 'active') {
                        isPro = true
                    } else if (subscription.status === 'active' && subscription.plan === 'pro') {
                        // Check if billing date hasn't lapsed past grace period
                        if (subscription.next_billing_date) {
                            isPro = new Date(subscription.next_billing_date) >= graceCutoff
                        } else {
                            isPro = true
                        }
                    } else if (subscription.status === 'cancelled' && subscription.plan === 'pro' && subscription.expires_at) {
                        isPro = new Date(subscription.expires_at) > new Date()
                    }
                }

                return {
                    ...profile,
                    subscription: subscription || null,
                    isPro,
                }
            })

            // Apply subscription status filter
            if (filters.status === 'pro') {
                usersWithSubs = usersWithSubs.filter(u => u.isPro === true)
            } else if (filters.status === 'free') {
                usersWithSubs = usersWithSubs.filter(u => u.isPro !== true)
            }

            console.log('Users with subscriptions:', usersWithSubs)

            setUsers(usersWithSubs)
            setPagination(prev => ({
                ...prev,
                total: filters.status === 'all' ? (count || usersWithSubs.length) : usersWithSubs.length
            }))
        } catch (error) {
            console.error('Error fetching users:', error)
            setUsers([])
        } finally {
            setLoading(false)
        }
    }

    // Export to CSV functionality
    const exportToExcel = async () => {
        setExporting(true)
        try {
            // Fetch all profiles
            let profileQuery = supabase
                .from('profiles')
                .select('*')
                .order('created_at', { ascending: false })

            if (filters.search) {
                profileQuery = profileQuery.ilike('email', `%${filters.search}%`)
            }
            if (filters.dateFrom) {
                profileQuery = profileQuery.gte('created_at', filters.dateFrom)
            }
            if (filters.dateTo) {
                profileQuery = profileQuery.lte('created_at', filters.dateTo + 'T23:59:59')
            }

            const { data: profiles, error: profileError } = await profileQuery

            if (profileError) throw profileError

            // Fetch all subscriptions
            const { data: subscriptions } = await supabase
                .from('subscriptions')
                .select('*')

            // Merge data
            let exportData = (profiles || []).map(profile => {
                const subscription = (subscriptions || []).find(s =>
                    s.user_id === profile.id || s.email === profile.email
                )
                // Use same validation logic as table display
                let isPro = false
                if (subscription) {
                    const GRACE_DAYS = 7
                    const graceCutoff = new Date()
                    graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)

                    if (subscription.plan === 'lifetime' && subscription.status === 'active') {
                        isPro = true
                    } else if (subscription.status === 'active' && subscription.plan === 'pro') {
                        if (subscription.next_billing_date) {
                            isPro = new Date(subscription.next_billing_date) >= graceCutoff
                        } else {
                            isPro = true
                        }
                    } else if (subscription.status === 'cancelled' && subscription.plan === 'pro' && subscription.expires_at) {
                        isPro = new Date(subscription.expires_at) > new Date()
                    }
                }

                // Compute effective status
                let effectiveStatus = subscription?.status || 'none'
                if (subscription?.status === 'active' && subscription.next_billing_date) {
                    const GRACE_DAYS = 7
                    const graceCutoff = new Date()
                    graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)
                    if (new Date(subscription.next_billing_date) < graceCutoff) {
                        effectiveStatus = 'expired'
                    }
                }

                return {
                    ...profile,
                    isPro,
                    subscriptionStatus: effectiveStatus,
                    subscriptionPlan: subscription?.plan || 'free',
                    subscriptionStarted: subscription?.started_at || null,
                }
            })

            // Apply filter
            if (filters.status === 'pro') {
                exportData = exportData.filter(u => u.isPro === true)
            } else if (filters.status === 'free') {
                exportData = exportData.filter(u => u.isPro !== true)
            }

            // Convert to CSV
            const csvData = convertToCSV(exportData)
            downloadCSV(csvData, `inkspot-users-${new Date().toISOString().split('T')[0]}.csv`)
        } catch (error) {
            console.error('Export error:', error)
            alert('Failed to export users. Please try again.')
        } finally {
            setExporting(false)
        }
    }

    const convertToCSV = (data) => {
        if (data.length === 0) return ''

        const headers = ['Email', 'Subscription Status', 'Plan', 'City', 'Country', 'Registered Date', 'Subscription Started']

        const rows = data.map(user => [
            user.email || '',
            user.subscriptionStatus || 'none',
            user.isPro ? 'Pro' : 'Free',
            user.city || '',
            user.country || '',
            formatDateForExport(user.created_at),
            formatDateForExport(user.subscriptionStarted),
        ])

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n')

        return csvContent
    }

    const downloadCSV = (csvContent, filename) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const formatDateForExport = (dateString) => {
        if (!dateString) return ''
        return new Date(dateString).toISOString().split('T')[0]
    }

    const getSubscriptionStatus = (user) => {
        if (user.isPro) {
            return {
                status: 'Pro',
                color: 'bg-gradient-to-r from-primary-500/30 to-accent-500/30 text-primary-300 border border-primary-500/30'
            }
        }

        // Show more granular status for non-pro users who have a subscription record
        if (user.subscription) {
            const sub = user.subscription

            // Check if billing date has lapsed (effectively expired even if status says active)
            if (sub.status === 'active' && sub.next_billing_date) {
                const GRACE_DAYS = 7
                const graceCutoff = new Date()
                graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)
                if (new Date(sub.next_billing_date) < graceCutoff) {
                    return {
                        status: 'Expired',
                        color: 'bg-red-500/20 text-red-300 border border-red-500/30'
                    }
                }
            }

            if (sub.status === 'expired') {
                return {
                    status: 'Expired',
                    color: 'bg-red-500/20 text-red-300 border border-red-500/30'
                }
            }
            if (sub.status === 'past_due') {
                return {
                    status: 'Past Due',
                    color: 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                }
            }
            if (sub.status === 'suspended') {
                return {
                    status: 'Suspended',
                    color: 'bg-red-500/20 text-red-300 border border-red-500/30'
                }
            }
            if (sub.status === 'cancelled') {
                // Check if still in grace period
                if (sub.expires_at && new Date(sub.expires_at) > new Date()) {
                    return {
                        status: 'Cancelling',
                        color: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                    }
                }
                return {
                    status: 'Cancelled',
                    color: 'bg-slate-600/50 text-slate-300 border border-slate-500/30'
                }
            }
        }

        return {
            status: 'Free',
            color: 'bg-slate-700/50 text-slate-300 border border-slate-600/30'
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    const totalPages = Math.ceil(pagination.total / pagination.perPage)

    // Handle upgrade user
    const handleUpgrade = async () => {
        if (!upgradeModal.user || !upgradeModal.plan) return

        setUpgrading(true)
        try {
            const now = new Date().toISOString()
            const isLifetime = upgradeModal.plan === 'lifetime'

            const nextBillingDate = isLifetime
                ? null
                : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

            const { error } = await supabase
                .from('subscriptions')
                .upsert({
                    user_id: upgradeModal.user.id,
                    email: upgradeModal.user.email,
                    plan: 'pro',
                    status: 'active',
                    started_at: now,
                    next_billing_date: nextBillingDate,
                    expires_at: null,
                    subscription_type: isLifetime ? 'lifetime' : 'monthly',
                }, { onConflict: 'email' })

            if (error) {
                console.error('Upgrade error:', error)
                alert('Failed to upgrade user. Please try again.')
            } else {
                await fetchUsers()
                setUpgradeModal({ open: false, user: null, plan: null })
            }
        } catch (error) {
            console.error('Upgrade error:', error)
            alert('Failed to upgrade user. Please try again.')
        } finally {
            setUpgrading(false)
        }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setActiveDropdown(null)
        if (activeDropdown) {
            document.addEventListener('click', handleClickOutside)
            return () => document.removeEventListener('click', handleClickOutside)
        }
    }, [activeDropdown])

    return (
        <div className="glass-card p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-white">Users</h3>
                        <p className="text-sm text-white/50">{pagination.total} total users</p>
                    </div>
                </div>

                {/* Filters & Export */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                            type="text"
                            placeholder="Search email..."
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:border-primary-500 focus:outline-none w-48"
                        />
                    </div>

                    {/* Status Filter */}
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="px-4 py-2 bg-white border border-white/20 rounded-lg text-slate-900 text-sm focus:border-primary-500 focus:outline-none font-medium"
                    >
                        <option value="all" className="bg-white text-slate-900">All Users</option>
                        <option value="pro" className="bg-white text-slate-900">Pro Only</option>
                        <option value="free" className="bg-white text-slate-900">Free Only</option>
                    </select>

                    {/* Date From */}
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-primary-500 focus:outline-none"
                    />
                    <span className="text-white/40">to</span>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-primary-500 focus:outline-none"
                    />

                    {/* Export Button */}
                    <button
                        onClick={exportToExcel}
                        disabled={exporting || users.length === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download className="w-4 h-4" />
                        {exporting ? 'Exporting...' : 'Export CSV'}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-white/10">
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/60">Email</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/60">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/60">Location</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/60">Registered</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/60">Subscription</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/60">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="6" className="text-center py-8">
                                    <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-8 text-white/40">
                                    No users found
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => {
                                const subStatus = getSubscriptionStatus(user)
                                return (
                                    <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center text-white text-sm font-medium">
                                                    {user.email?.charAt(0).toUpperCase() || '?'}
                                                </div>
                                                <span className="text-white text-sm">{user.email}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${subStatus.color}`}>
                                                {subStatus.status === 'Pro' && <Crown className="w-3 h-3" />}
                                                {subStatus.status}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            {user.city || user.country ? (
                                                <div className="flex items-center gap-1 text-sm text-white/70">
                                                    <MapPin className="w-3 h-3 text-white/40" />
                                                    {[user.city, user.country].filter(Boolean).join(', ')}
                                                </div>
                                            ) : (
                                                <span className="text-sm text-white/30">-</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-white/70">
                                            {formatDate(user.created_at)}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-white/70">
                                            {user.subscription ? (
                                                <div>
                                                    <span className={`text-xs ${
                                                        (() => {
                                                            const sub = user.subscription
                                                            // Check effective status
                                                            if (sub.status === 'active' && sub.next_billing_date) {
                                                                const GRACE_DAYS = 7
                                                                const graceCutoff = new Date()
                                                                graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)
                                                                if (new Date(sub.next_billing_date) < graceCutoff) return 'text-red-400'
                                                            }
                                                            if (sub.status === 'active') return 'text-green-400'
                                                            if (sub.status === 'cancelled') return 'text-yellow-400'
                                                            if (sub.status === 'expired') return 'text-red-400'
                                                            if (sub.status === 'past_due') return 'text-orange-400'
                                                            if (sub.status === 'suspended') return 'text-red-400'
                                                            return 'text-white/50'
                                                        })()
                                                    }`}>
                                                        {(() => {
                                                            const sub = user.subscription
                                                            // Show effective status, not just DB status
                                                            if (sub.status === 'active' && sub.next_billing_date) {
                                                                const GRACE_DAYS = 7
                                                                const graceCutoff = new Date()
                                                                graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)
                                                                if (new Date(sub.next_billing_date) < graceCutoff) return 'expired'
                                                            }
                                                            return sub.status
                                                        })()}
                                                        {user.subscription.subscription_type === 'lifetime' && ' (Lifetime)'}
                                                    </span>
                                                    {user.subscription.started_at && (
                                                        <span className="text-white/40 ml-2">
                                                            since {formatDate(user.subscription.started_at)}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-white/30">-</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setActiveDropdown(activeDropdown === user.id ? null : user.id)
                                                    }}
                                                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                                                >
                                                    <MoreVertical className="w-4 h-4 text-white/60" />
                                                </button>
                                                {activeDropdown === user.id && (
                                                    <div className="absolute right-0 top-full mt-1 w-48 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setUpgradeModal({ open: true, user, plan: 'pro' })
                                                                setActiveDropdown(null)
                                                            }}
                                                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-white hover:bg-white/10 transition-colors"
                                                        >
                                                            <Zap className="w-4 h-4 text-primary-400" />
                                                            Upgrade to Pro
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setUpgradeModal({ open: true, user, plan: 'lifetime' })
                                                                setActiveDropdown(null)
                                                            }}
                                                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-white hover:bg-white/10 transition-colors"
                                                        >
                                                            <Infinity className="w-4 h-4 text-accent-400" />
                                                            Upgrade to Lifetime
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-white/50">
                        Page {pagination.page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === totalPages}
                            className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Upgrade Confirmation Modal */}
            {upgradeModal.open && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">Confirm Upgrade</h3>
                            <button
                                onClick={() => setUpgradeModal({ open: false, user: null, plan: null })}
                                className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                            >
                                <X className="w-5 h-5 text-white/60" />
                            </button>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 mb-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${upgradeModal.plan === 'lifetime'
                                        ? 'bg-accent-500/20'
                                        : 'bg-primary-500/20'
                                    }`}>
                                    {upgradeModal.plan === 'lifetime'
                                        ? <Infinity className="w-5 h-5 text-accent-400" />
                                        : <Zap className="w-5 h-5 text-primary-400" />
                                    }
                                </div>
                                <div>
                                    <p className="text-white font-medium">
                                        {upgradeModal.plan === 'lifetime' ? 'Lifetime Pro' : 'Pro Monthly'}
                                    </p>
                                    <p className="text-sm text-white/50">
                                        {upgradeModal.plan === 'lifetime'
                                            ? 'Permanent access, no billing'
                                            : 'Monthly subscription'
                                        }
                                    </p>
                                </div>
                            </div>

                            <p className="text-white/70">
                                Are you sure you want to upgrade <span className="text-white font-medium">{upgradeModal.user?.email}</span> to {upgradeModal.plan === 'lifetime' ? 'Lifetime Pro' : 'Pro'} membership?
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setUpgradeModal({ open: false, user: null, plan: null })}
                                className="flex-1 px-4 py-3 rounded-xl bg-white/5 text-white font-medium hover:bg-white/10 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpgrade}
                                disabled={upgrading}
                                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors disabled:opacity-50 ${upgradeModal.plan === 'lifetime'
                                        ? 'bg-gradient-to-r from-accent-500 to-primary-500 text-white hover:opacity-90'
                                        : 'bg-primary-500 text-white hover:bg-primary-600'
                                    }`}
                            >
                                {upgrading ? 'Upgrading...' : 'Confirm Upgrade'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
